import { MESSAGE_ACTION, selMessage } from "@selector/shared";
import { SelMessage } from "@selector/shared/components";
import { ProjectColoredLink } from "@selector/shared/components";
import { checkProjectFileExists, downloadProjectFile } from "@selector/shared/jsx";
import type { FC, ReactElement, ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  ActionButtonSize,
  HStack,
  InfoToggletip,
  VStack,
} from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { pluralizeWord } from "@/app-utils/string.ts";
import {
  ActivityTypeEnum,
  type Organism,
  type Project,
  type ProjectActivity,
  type ProjectFile,
  type Region,
  type RegionFlag,
  type User,
} from "@/graphql.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import { getUserTooltipDisplayName } from "@/modules/users/utils/user.js";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { openDatasetInAnalyticalViewer } from "@/redux/modules/dataset-viewer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { PLAYBOOK_TYPE } from "@/settings/settings.ts";

import { parseSmartLink } from "../core/parseSmartLink.tsx";
import ProjectActivityAttachments from "./ProjectActivityAttachments.js";
import styles from "./ProjectActivityMessage.module.css";

export interface ProjectActivityMessageProps {
  activity: ProjectActivity;
  users: User[];
  forNotification: boolean;
  notificationId?: string | number;
}

const ProjectActivityMessage: FC<ProjectActivityMessageProps> = (props) => {
  const { activity, users, forNotification } = props;
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const onDownloadProjectFile = (projectFile: ProjectFile) =>
    void checkProjectFileExists(projectFile.id).then((exists) => {
      if (exists) {
        downloadProjectFile(projectFile);
      } else {
        notify.warning(
          {
            title: selMessage({
              id: "file.download.failed.title",
            }),
            children: selMessage({
              id: "file.download.failed.children",
              values: { fileNames: projectFile.fileName, filesCount: 1 },
            }),
          },
          { autoClose: 5000 },
        );
      }
    });
  const onOpenDataset = (datasetId: number) => dispatch(openDatasetInAnalyticalViewer(datasetId));

  if (!activity) {
    return null;
  }

  let message: ReactNode = null;
  const activityKey = `act--${activity.type}-${activity.id}`;

  /*
   *
   * CAUTION!!!!
   * Never hard-wrap the following message strings inside the text composing, otherwise blanks may get lost!
   *
   * Never write fragile code that crosses the white vertical wrapping line of your IDE!!!
   * A simple auto-format (CTRL+ALT+L) will break this code.
   *
   */
  switch (activity.type) {
    case ActivityTypeEnum.CreatedProject:
      {
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.created"}
              values={{
                publicProject: activity.project?.isPublic,
                projectLabel: getProjectLabel(),
                projectLink: displayProject(activity.project, activity.projectName),
                usergroup: activity.project?.userGroup.name,
                usersCount: activity.affectedUsers.length,
                membersList: displayUsersList(activityKey, activity.affectedUsers),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.AddedMember:
      {
        const userlist = displayUsersList(activityKey, activity.affectedUsers);
        message = (
          <span key={activityKey}>
            {forNotification ? (
              <SelMessage
                id={"activity.project.addedYou"}
                values={{
                  projectLink: displayProject(activity.project, activity.projectName),
                }}
              />
            ) : (
              <SelMessage
                id={"activity.project.addedMembers"}
                values={{ members: userlist, projectLabel: getProjectLabel() }}
              />
            )}
          </span>
        );
      }
      break;
    case ActivityTypeEnum.DeletedMember:
      {
        const userlist = displayUsersList(activityKey, activity.affectedUsers);
        message = (
          <span key={activityKey}>
            {forNotification ? (
              <SelMessage
                id={"activity.project.removedYou"}
                values={{
                  projectLink: displayProject(activity.project, activity.projectName),
                }}
              />
            ) : (
              <SelMessage id={"activity.project.removedMembers"} values={{ members: userlist }} />
            )}
          </span>
        );
      }
      break;
    case ActivityTypeEnum.Comment:
      message = (
        <span
          title={forNotification ? `View this comment inside the ${getProjectLabel()} activity feed` : null}
          key={activityKey}
          className={styles.comment}
        >
          {formatMessageBody(activity.commentBody, users)}
        </span>
      );
      break;
    case ActivityTypeEnum.SavedGroup:
      {
        const groupsCount: number = activity.groups.length + activity.deletedGroups.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.savedItem"}
              values={{
                count: groupsCount,
                shared: !activity.isPrivate,
                itemLabel: pluralizeWord("group", groupsCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.AddedSampleParent:
    case ActivityTypeEnum.ModifiedSampleParent:
      {
        let action = null;
        switch (activity.type) {
          case ActivityTypeEnum.AddedSampleParent:
            action = MESSAGE_ACTION.ADD;
            break;
          case ActivityTypeEnum.ModifiedSampleParent:
            action = MESSAGE_ACTION.MODIFY;
            break;
        }
        const parentsCount: number = activity.parents.length;
        message = (
          <SelMessage
            id={"activity.project.sampleParent"}
            values={{
              action: action,
              count: parentsCount || 1,
              itemLabel: pluralizeWord("sample parent", parentsCount),
              emph: (chunk) =>
                parentsCount === 0 ? (
                  <strong title={"This sample parent has been deleted"} className={styles.inaccessible}>
                    {chunk}
                  </strong>
                ) : (
                  chunk
                ),
            }}
          />
        );
      }
      break;
    case ActivityTypeEnum.SavedView:
      {
        const viewsCount: number = activity.views.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.savedItem"}
              values={{
                count: viewsCount,
                shared: !activity.isPrivate,
                itemLabel: pluralizeWord("view", viewsCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.AddedProjectFile:
      {
        const resourcesCount: number = activity.projectFiles.length;
        const fileLabel = pluralizeWord("file", resourcesCount);
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.savedItem"}
              values={{
                count: resourcesCount,
                shared: null,
                itemLabel: `${getProjectLabel()} ${fileLabel}`,
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.DeletedGroup:
      {
        const deletedGroupsCount: number = activity.deletedGroups.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.deletedItem"}
              values={{
                count: deletedGroupsCount,
                itemLabel: pluralizeWord("group", deletedGroupsCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.DeletedView:
      {
        const deletedViewsCount: number = activity.views.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.deletedItem"}
              values={{
                count: deletedViewsCount,
                itemLabel: pluralizeWord("view", deletedViewsCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.DeletedProjectFile:
      {
        const deletedResourcesCount: number = activity.projectFiles.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.deletedItem"}
              values={{
                count: deletedResourcesCount,
                itemLabel: `${getProjectLabel()} ${pluralizeWord("file", deletedResourcesCount)}`,
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.AddedGenomeFlag:
      {
        const addedFlagsCount: number = activity.flags.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.addedFlag"}
              values={{
                count: addedFlagsCount,
                itemLabel: pluralizeWord("flag", addedFlagsCount),
                flagLocation: displayFlagLocation(activity.flags[0]),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.DeletedGenomeFlag:
      {
        const deletedFlagsCount: number = activity.flags.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.deletedFlag"}
              values={{
                count: deletedFlagsCount,
                itemLabel: pluralizeWord("flag", deletedFlagsCount),
                flagLocation: displayFlagLocation(activity.flags[0]),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.ModifiedGenomeFlag:
      {
        const modifiedFlagsCount: number = activity.flags.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.modifiedFlag"}
              values={{
                count: modifiedFlagsCount,
                itemLabel: pluralizeWord("flag", modifiedFlagsCount),
                flagLocation: displayFlagLocation(activity.flags[0]),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.ModifiedProjectFile: // #SEL5-325 a project resource cannot be published, so this activity is only about overwriting the file content !
      {
        const modifiedResourcesCount: number = activity.projectFiles.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.modifiedItem"}
              values={{
                count: modifiedResourcesCount,
                itemLabel: `${getProjectLabel()} ${pluralizeWord("file", modifiedResourcesCount)}`,
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.ModifiedGroup:
      {
        const modifiedGroupsCount: number = activity.groups.length + activity.deletedGroups.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.publishedItem"}
              values={{
                count: modifiedGroupsCount,
                itemLabel: pluralizeWord("group", modifiedGroupsCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.ModifiedView:
      {
        const modifiedViewsCount: number = activity.views.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.publishedItem"}
              values={{
                count: modifiedViewsCount,
                itemLabel: pluralizeWord("view", modifiedViewsCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.ModifiedProject:
      message = (
        <span key={activityKey}>
          <SelMessage
            id={"activity.project.modifiedProject"}
            values={{
              projectLabel: getProjectLabel(),
              projectLink: displayProject(activity.project, activity.projectName),
            }}
          />
        </span>
      );
      break;
    case ActivityTypeEnum.EditProject:
      message = forNotification ? (
        <span key={activityKey}>
          <SelMessage
            id={"activity.project.editedProject.notification"}
            values={{
              projectLabel: getProjectLabel(),
              projectLink: displayProject(activity.project, activity.projectName),
            }}
          />
        </span>
      ) : (
        <span key={activityKey}>
          <SelMessage
            id={"activity.project.editedProject"}
            values={{
              projectLabel: getProjectLabel(),
            }}
          />
        </span>
      );
      break;
    case ActivityTypeEnum.ExecutedPlaybook:
      const isBlastPlaybook: boolean = activity.playbookEvent?.playbook.type.id === PLAYBOOK_TYPE.BLAST;
      message = (
        <span key={activityKey}>
          {isBlastPlaybook ? (
            <SelMessage
              id={"activity.project.executedBlastJob"}
              values={{
                jobName: activity.playbookEvent?.job.name,
                link: (chunk) => <Link to={`/event/${activity.playbookEvent?.id}`}>{chunk}</Link>,
              }}
            />
          ) : (
            <SelMessage
              id={"activity.project.executedPlaybook"}
              values={{
                playbookName: `${activity.playbookEvent?.playbook.name} ${
                  activity.playbookEvent?.alias ? "(" + activity.playbookEvent?.alias + ")" : ""
                }`,
                link: (chunk) => <Link to={`/event/${activity.playbookEvent?.id}`}>{chunk}</Link>,
              }}
            />
          )}
        </span>
      );
      break;
    case ActivityTypeEnum.AddedCoordinateList:
      {
        const addedCoordListCount: number = activity.coordinateLists.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.addedCoordinateList"}
              values={{
                count: addedCoordListCount,
                genome:
                  addedCoordListCount > 0 && activity.coordinateLists[0].organism
                    ? activity.coordinateLists[0].organism.name
                    : undefined, // coordinateList can be empty
                itemLabel: pluralizeWord("coordinate list", addedCoordListCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.DeletedCoordinateList:
      {
        const deletedCoordListCount: number = activity.coordinateLists.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.deletedCoordinateList"}
              values={{
                count: deletedCoordListCount,
                genome:
                  deletedCoordListCount > 0 && activity.coordinateLists[0].organism
                    ? activity.coordinateLists[0].organism.name
                    : undefined, // coordinateList can be empty
                itemLabel: pluralizeWord("coordinate list", deletedCoordListCount),
              }}
            />
          </span>
        );
      }
      break;
    case ActivityTypeEnum.ModifiedCoordinateList:
      {
        const modifiedCoordListCount: number = activity.coordinateLists.length;
        message = (
          <span key={activityKey}>
            <SelMessage
              id={"activity.project.modifiedCoordinateList"}
              values={{
                count: modifiedCoordListCount,
                genome:
                  modifiedCoordListCount > 0 && activity.coordinateLists[0].organism
                    ? activity.coordinateLists[0].organism.name
                    : undefined, // coordinateList can be empty
                itemLabel: pluralizeWord("coordinate list", modifiedCoordListCount),
              }}
            />
          </span>
        );
      }
      break;
    default:
      message = (
        <span key={activityKey}>
          Type {activity.type} <b>[NOT IMPLEMENTED]</b>
        </span>
      );
      break;
  }

  return (
    <VStack>
      <div>
        {message}
        <ProjectActivityAttachments
          activity={activity}
          onDownloadProjectFile={onDownloadProjectFile}
          onOpenDataset={onOpenDataset}
        />
      </div>
      {props.notificationId && activity.project && (
        <div>
          <ActionButton
            label={undefined}
            onPress={() =>
              navigate(
                `/project/${activity.project?.id}?activityid=${activity.id}&notificationid=${props.notificationId}`,
              )
            }
            intent={ActionButtonIntent.Outlined}
            size={ActionButtonSize.Small}
            icon={"arrow_forward"}
            iconPlacement={ActionButtonIconPlacement.Right}
          >
            <ProjectColoredLink project={activity.project} noLink />
          </ActionButton>
        </div>
      )}
    </VStack>
  );
};

function displayUsersList(keyPrefix: string, users: User[]): ReactElement[] {
  const limit = 20;
  return users.map((user: User, index: number) => {
    if (index > limit) {
      return;
    } else if (index === limit) {
      return <span key={`${keyPrefix}-userlist-ellipsis`}>{"... (" + (users.length - index) + " more)"}</span>;
    }
    return (
      <span title={getUserTooltipDisplayName(user)} key={`${keyPrefix}-userlist-${index}`}>
        <b>{user.username}</b>
        {index < users.length - 1 ? ", " : ""}
      </span>
    );
  });
}

// projectName is provided as part of the activity in case it has been deleted or access it not allowed anymore
function displayProject(project: Project, projectName: string) {
  if (project) {
    return (
      <strong>
        <ProjectColoredLink project={project} style={{ width: "auto" }} noLink />
      </strong>
    );
  } else {
    return (
      <HStack>
        <strong>{projectName}</strong>
        <InfoToggletip content={`You cannot access this ${getProjectLabel(false, false)}`} />
      </HStack>
    );
  }
}

function displayFlagLocation(flag: RegionFlag): ReactElement {
  if (!flag) {
    return null;
  }
  const region: Region = flag.region;
  const genome: Organism = region.contig.organism;
  const rangeStart: string = formatNumberByThousands(region.range.start);
  const rangeStop: string = formatNumberByThousands(region.range.stop);
  const location = `${region.contig.sequenceId}:${rangeStart}-${rangeStop}`;

  return (
    <span>
      {genome.name} <i>({genome.species.name})</i> at{" "}
      <span style={{ whiteSpace: "nowrap", wordBreak: "keep-all" }}>{location}</span>
    </span>
  );
}

function formatMessageBody(text: string, users: User[]): ReactElement | string {
  if (!text || !text.includes("@")) {
    return <>{parseSmartLink(text)}</>;
  }
  // tokenizing works better without the lookahead operator in the regex for splitting, also recognizes '@andreas,...'
  const rxBeforeTag = /(@\w+)/;
  const tokens = text.split(rxBeforeTag);
  const out: ReactNode[] = [];

  for (const token of tokens) {
    if (token.startsWith("@")) {
      const username = token.slice(1);
      const user = findUser(username, users);
      const tooltip = user ? getUserTooltipDisplayName(user) : undefined;

      out.push(
        <strong key={`at-user-${username}`} title={tooltip}>
          @{username}
        </strong>,
      );
    } else {
      out.push(parseSmartLink(token));
    }
  }

  return <>{out}</>;
}

function findUser(username: string, users: User[]): User | null {
  if (users == null || users.length === 0) {
    return null;
  }

  const ret = users.find((u) => u.username === username);

  if (ret == undefined) {
    return null;
  }

  return ret;
}

export { ProjectActivityMessage };
